<script setup lang="ts">
import flatpickr from 'flatpickr'
import { Slovak } from 'flatpickr/dist/l10n/sk.js'
import rangePlugin from 'flatpickr/dist/plugins/rangePlugin'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { Calendar, X } from 'lucide-vue-next'
import dayjs from 'dayjs'

const props = withDefaults(defineProps<{
  modelValue: { start: string; end: string } | null
  startPlaceholder?: string
  endPlaceholder?: string
}>(), { startPlaceholder: 'Od', endPlaceholder: 'Do' })

const emit = defineEmits<{
  (e: 'update:modelValue', value: { start: string; end: string } | null): void
}>()

const startRef = ref<HTMLInputElement | null>(null)
const endRef = ref<HTMLInputElement | null>(null)
let fp: flatpickr.Instance | null = null
let lastEmitted = ''

const fieldClass =
  'h-9 w-40 cursor-pointer rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700 shadow-theme-xs outline-none hover:bg-gray-50 focus:border-brand-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700/50'

const hasValue = computed(() => Boolean(props.modelValue?.start || props.modelValue?.end))

const parseField = (el: HTMLInputElement | null): string => {
  const raw = el?.value?.trim()
  if (!raw || !fp) return ''
  const parsed = fp.parseDate(raw, 'd.m.Y')
  return parsed ? dayjs(parsed).format('YYYY-MM-DD') : ''
}

const emitFromFields = () => {
  const start = parseField(startRef.value)
  const end = parseField(endRef.value)
  const value = !start && !end ? null : { start, end }
  const key = JSON.stringify(value)
  if (key === lastEmitted) return
  lastEmitted = key
  emit('update:modelValue', value)
}

const clearDates = () => {
  fp?.clear()
  if (startRef.value) startRef.value.value = ''
  if (endRef.value) endRef.value.value = ''
  const key = JSON.stringify(null)
  if (key === lastEmitted) return
  lastEmitted = key
  emit('update:modelValue', null)
}

const syncFromModel = () => {
  if (!fp) return
  const value = props.modelValue
  lastEmitted = JSON.stringify(value ?? null)
  if (!value?.start && !value?.end) {
    if (fp.selectedDates.length) fp.clear()
    return
  }
  if (value.start && value.end) {
    fp.setDate([dayjs(value.start).toDate(), dayjs(value.end).toDate()], true)
  }
}

onMounted(() => {
  if (!startRef.value || !endRef.value) return

  const instance = flatpickr(startRef.value, {
    dateFormat: 'd.m.Y',
    allowInput: true,
    static: true,
    monthSelectorType: 'static',
    locale: Slovak,
    plugins: [rangePlugin({ input: endRef.value })],
    prevArrow:
      '<svg class="stroke-current" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.5 15L7.5 10L12.5 5" stroke="" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    nextArrow:
      '<svg class="stroke-current" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.5 15L12.5 10L7.5 5" stroke="" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    onChange: (selectedDates) => {
      if (selectedDates.length === 2) {
        emitFromFields()
      }
    },
    onClose: () => {
      emitFromFields()
    },
  })

  fp = Array.isArray(instance) ? instance[0] : instance
  syncFromModel()
})

watch(() => [props.modelValue?.start, props.modelValue?.end], syncFromModel)

onBeforeUnmount(() => {
  fp?.destroy()
  fp = null
})
</script>

<template>
  <div class="inline-flex items-center gap-2">
    <div class="relative inline-flex items-center">
      <Calendar
        class="pointer-events-none absolute start-3 top-1/2 z-10 -translate-y-1/2 text-gray-500 dark:text-gray-400"
      />
      <input
        ref="startRef"
        :placeholder="startPlaceholder"
        :class="[fieldClass, 'ps-9']"
        @blur="emitFromFields"
      />
    </div>
    <div class="relative inline-flex items-center">
      <Calendar
        class="pointer-events-none absolute start-3 top-1/2 z-10 -translate-y-1/2 text-gray-500 dark:text-gray-400"
      />
      <input
        ref="endRef"
        :placeholder="endPlaceholder"
        :class="[fieldClass, 'ps-9']"
        @blur="emitFromFields"
      />
    </div>
    <button
      v-if="hasValue"
      type="button"
      class="inline-flex size-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 shadow-theme-xs hover:bg-gray-50 hover:text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700/50 dark:hover:text-gray-300"
      aria-label="Vymazať rozsah dátumov"
      @click="clearDates"
    >
      <X class="size-4" />
    </button>
  </div>
</template>
